import React, {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useFormik} from "formik";
import * as Yup from "yup";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Spinner,
  UncontrolledTooltip
} from "reactstrap";

import {
  checkPaymentStatus,
  createPaymentLink,
  getExternalUsersCount
} from "../../store/subscriptions/actions";

import {showNotification} from "../../components/Common/notification";

const ProductSubscriptionPlan = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const {payment_url, error, payment_status, externalUsersCount} = useSelector(state => ({
    payment_url: state.Subscriptions.payment_url,
    error: state.Subscriptions.error,
    externalUsersCount: state.Subscriptions.externalUsersCount || [],
    payment_status: state.Subscriptions.payment_status,
  }));
  const {subscriptionDetail} = useSelector(state => ({
    users: state.Organization.users || [],
    subscriptionDetail: state.Organization.subscription || []
  }));

  useEffect(() => {
    if (payment_status && payment_status === "paid") {
      showNotification("success", 'Payment was successful!');
    }
  }, [payment_status]);

  useEffect(() => {
    dispatch(getExternalUsersCount());
  }, [dispatch]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has('client_reference_id')) {
      const clientReferenceId = queryParams.get('client_reference_id');
      dispatch(checkPaymentStatus({client_reference_id: clientReferenceId}));
    }
  }, []);

  useEffect(() => {
    if (payment_url) {
      setLoading(false);
    }
  }, [payment_url]);

  useEffect(() => {
    if (error) {
      setLoading(false);
      showNotification("error", error);
    }
  }, [error]);

  function formatDate(dateString) {
    if (!dateString) return "";

    const dateObj = new Date(dateString);

    if (isNaN(dateObj)) {
      return "Invalid Date";
    }

    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();

    return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
  }

  const validationSchema = Yup.object({
    subscriberCount: Yup.number()
      .required("Number of subscribers is required")
      .min(externalUsersCount, `Must be at least ${externalUsersCount}`)
      .integer("Must be a whole number")
      .positive("Must be a positive number")
  });
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      subscriberCount: externalUsersCount,
      billingCycle: "monthly"
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setLoading(true);

      const payment_payload = {
        number_of_subscribers: values.subscriberCount,
        billing_cycle: values.billingCycle
      };
      dispatch(createPaymentLink(payment_payload));
    }
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <h2 title="Product Subscription Plan"/>
          <Row className="mb-4">
            <Col lg={12}>
              <div className="page-title-box">
                <h2 className="mb-0">Manage Subscription</h2>
                <p className="text-white mt-2">Manage your product subscription plan</p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={6} className="d-flex">
              <Card className="bg-transparent border flex-fill">
                <CardBody className="pt-0 account-margin">
                  <h4 className={"text-primary mb-3"}>Current Subscription Plan</h4>
                  <div className="table-responsive">
                    <table className="table-purple-org">
                      <tbody>
                      <tr>
                        <td scope="col">Subscription Name</td>
                        <td scope="col">{subscriptionDetail?.master_product_plan?.name}</td>
                      </tr>
                      <tr>
                        <td scope="col">Current User Count</td>
                        <td scope="col">{externalUsersCount || 0}</td>
                      </tr>
                      <tr>
                        <td scope="col">Subscribed User Count</td>
                        <td scope="col">{subscriptionDetail?.number_of_users}</td>
                      </tr>
                      <tr>
                        <td scope="col">Number of Resources</td>
                        <td scope="col">{subscriptionDetail?.master_product_plan?.number_of_resources}</td>
                      </tr>
                      <tr>
                        <td scope="col">Number of Scans</td>
                        <td scope="col">{subscriptionDetail?.master_product_plan?.number_of_scans}</td>
                      </tr>
                      <tr>
                        <td scope="col">Number of Deployments</td>
                        <td
                          scope="col">{subscriptionDetail?.master_product_plan?.number_of_alert_deployments}</td>
                      </tr>
                      <tr>
                        <td scope="col"><b>Plan start
                          date: </b> {formatDate(subscriptionDetail?.plan_start_date)}</td>
                        <td scope="col"><b>Plan end date: </b> {formatDate(subscriptionDetail?.plan_end_date)}
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col lg={6} className="d-flex">
              <Card className="bg-transparent border flex-fill">
                <CardBody className="pt-0 account-margin">
                  <h4 className="text-primary mb-4">Purchase Plan</h4>
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <Row className="mb-1 mt-3">
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label" for="billingCycle">Billing Cycle</Label>
                            <Input
                              type="select"
                              name="billingCycle"
                              id="billingCycle"
                              className="form-control"
                              value={validation.values.billingCycle}
                              onChange={validation.handleChange}
                            >
                              <option value="monthly">Monthly</option>
                              <option value="annually">Annually (Save 10%)</option>
                            </Input>
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label" for="subscriberCount">
                              Number of Users
                              <i
                                className="mdi mdi-information-outline text-warning ms-1"
                                id="subscriberTooltip"
                              />
                              <UncontrolledTooltip
                                placement="top"
                                target="subscriberTooltip"
                                style={{
                                  border: "2px solid #f1b44c",
                                  borderRadius: "10px",
                                  backgroundColor: "#2a3042",
                                  zIndex: 1,
                                }}
                              >
                                Minimum subscribers must equal or exceed your current user count ({externalUsersCount})
                              </UncontrolledTooltip>
                            </Label>
                            <Input
                              name="subscriberCount"
                              id="subscriberCount"
                              className="form-control"
                              placeholder="Enter number of users"
                              type="number"
                              min={externalUsersCount}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.subscriberCount || ""}
                              invalid={
                                validation.touched.subscriberCount &&
                                validation.errors.subscriberCount ? true : false
                              }
                            />
                            {validation.touched.subscriberCount && validation.errors.subscriberCount ? (
                              <FormFeedback type="invalid">{validation.errors.subscriberCount}</FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                      <div className="mt-1 p-2 bg-light border border-info rounded" style={{ width: "fit-content" }}>
                        <i className="mdi mdi-information text-info me-2"></i>
                        <small className="text-white ">Temperstack support users are excluded from billing</small>
                      </div>
                      <div className="mt-4 text-align">
                        <Button
                          color="success"
                          type="submit"
                          className="login-button"
                          disabled={loading}
                          style={{width: "auto"}}
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2"/>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-lock-alt font-size-20 align-middle me-2"/> Pay Securely
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProductSubscriptionPlan;